import * as FileSaver from 'file-saver';
import PDFMerger from 'pdf-merger-js/browser';
import { utils as utilsXLSX, write as writeXLSX } from 'xlsx';

import { apiUrl } from '~/constants/environment';

import { downloadFile } from '@vestigas/utils';
import axios from '~/utils/api-client';
import { dateUtils } from '~/utils/dateUtils';
import FunctionUtils from '~/utils/functionUtils';
import i18n from '~/utils/i18n';
import { LOG, log } from '~/utils/logging';
import { handlePromise } from '~/utils/promise';

import { dateRangeOptions } from '~/ui/molecules/DateInput/constants';

import ToastService from './toast.service';

const API_URL = `${apiUrl}/asset/`;

class ExportService {
  constructor(props) {
    this.exportingDeliveryNotes = false;
    this.multiDeliveryNoteDownloadToast = null;

    this.DOWNLOAD_OPTION = {
      MERGE: 'merge',
      ZIP: 'zip',
    };
  }

  /**
   * Create a specific delivery note as PDF
   *
   * @param {*} deliveryNoteId
   * @param {Object} options
   * @param { "original" | "original_enhanced" | "docx" } options.doctype
   * @param {boolean} options.includeAttachments
   * @returns
   */
  async createSpecificDeliveryNoteAsPDF(deliveryNoteId, options) {
    return axios.get(`${API_URL + deliveryNoteId}/request_pdf`, {
      params: {
        doctype: options.doctype,
        include_attachments: options.includeAttachments ?? true,
      },
    });
  }

  /**
   *
   * @param {string} deliveryNoteId
   * @param {Object} options
   * @param { "original" | "original_enhanced" | "docx" } options.doctype
   * @param {boolean} options.includeAttachments
   * @returns
   */
  async getSpecificDeliveryNoteAsPDF(deliveryNoteId, options) {
    return axios.get(`${API_URL + deliveryNoteId}/get_pdf`, {
      responseType: 'blob',
      params: {
        doctype: options.doctype,
        include_attachments: options.includeAttachments ?? true,
      },
    });
  }

  /**
   * Export a specific delivery note as PDF
   *
   * @param {string} deliveryNoteId
   * @param {Object} options
   * @param { "original" | "original_enhanced" | "docx" } options.doctype
   * @param {boolean} options.includeAttachments
   * @returns
   */
  async exportSpecificDeliveryNoteAsPDF(deliveryNoteId, options) {
    const [, error1] = await handlePromise(
      this.createSpecificDeliveryNoteAsPDF(deliveryNoteId, options),
    );

    // In case of a 409 error, everything is ok and the PDF can be downloaded.
    if (error1 && error1.response?.status !== 409) {
      log.error({
        message: `Failed to request PDF creation of delivery note. id: ${
          deliveryNoteId
        }`,
        error: error1,
      });
      log.productAnalyticsEvent(
        'Failed to download delivery note PDF',
        LOG.FEATURE.PDF_DOWNLOAD,
        LOG.TYPE.ERROR,
      );
      throw error1;
    }

    let file = null;

    while (!file) {
      const [response2, error2] = await handlePromise(
        this.getSpecificDeliveryNoteAsPDF(deliveryNoteId, options),
      );

      if (error2) {
        log.error({
          message: `Failed to download PDF of delivery note. id: ${deliveryNoteId}`,
          error: error2,
        });
        log.productAnalyticsEvent(
          'Failed to download delivery note PDF',
          LOG.FEATURE.PDF_DOWNLOAD,
          LOG.TYPE.ERROR,
        );
        throw error2;
      }

      // If the server returns 202, it means that the PDF is not yet ready to be downloaded.
      // Therefore, wait for 1 second and then request PDF again.
      if (response2.status === 202) {
        await FunctionUtils.timer(1000);
        continue;
      }

      file = new Blob([response2.data], { type: 'application/pdf' });
    }

    return file;
  }

  /**
   * helper function for multiple delivery notes
   *
   * @param {string[]} deliveryNoteIds
   * @param {Object} options
   * @param { "original" | "original_enhanced" | "docx" } options.doctype
   * @param {boolean} options.includeAttachments
   * @returns
   */
  async exportMultipleDeliveryNotesAsPDF(deliveryNoteIds, options) {
    const blobArray = [];
    const failedPdfs = [];

    for (let index = 0; index < deliveryNoteIds.length; index++) {
      const deliveryNoteId = deliveryNoteIds[index];

      const [file, error] = await handlePromise(
        this.exportSpecificDeliveryNoteAsPDF(deliveryNoteId, options),
      );

      if (error) {
        failedPdfs.push(deliveryNoteId);
        log.error({
          message: `Failed to export PDF of delivery note. id: ${deliveryNoteId}`,
          error,
        });
        log.productAnalyticsEvent(
          'Failed to download delivery note PDF',
          LOG.FEATURE.PDF_DOWNLOAD,
          LOG.TYPE.ERROR,
        );
        continue;
      }

      blobArray.push({ file, id: deliveryNoteId });

      ToastService.promise(
        this.multiDeliveryNoteDownloadToast.promise,
        [
          `${this.multiDeliveryNoteDownloadToast.loadingLines[0]} (${
            index + 1
          }/${deliveryNoteIds.length})`,
        ],
        this.multiDeliveryNoteDownloadToast.successLines,
        this.multiDeliveryNoteDownloadToast.errorLines,
        this.multiDeliveryNoteDownloadToast.id,
      );
    }

    if (blobArray.length === 0) {
      throw new Error('Empty blobArray');
    }

    return [blobArray, failedPdfs];
  }

  /**
   * this function downloads the selected PDF files of delivery notes and zips them
   *
   * @param {string[]} ids
   * @param {string} downloadOption
   * @param {Object} deliveryNoteNumbers { [deliveryNoteId]: deliveryNoteNumber }
   * @param {Object} options
   * @param { "original" | "original_enhanced" | "docx" } options.doctype
   * @param {boolean} options.includeAttachments
   * @returns
   */
  exportDeliveryNotes = async (
    ids,
    downloadOption,
    options = {
      doctype: 'docx',
      includeAttachments: true,
    },
    deliveryNoteNumbers = {},
  ) => {
    log.info({
      message: 'Download delivery notes as PDF',
      data: { rowSelectionModel: ids },
      breadcrumb: {
        category: LOG.BREADCRUMB.USER_ACTION.CATEGORY,
        event_id: LOG.BREADCRUMB.USER_ACTION.KEY,
        message: LOG.BREADCRUMB.USER_ACTION.DEFAULT_MESSAGE,
        type: LOG.BREADCRUMB.USER_ACTION.TYPE,
      },
    });

    if (this.exportingDeliveryNotes) {
      ToastService.info(
        ['Bitte abwarten bis der aktuelle Download abgeschlossen ist.'],
        ToastService.ID.DLN_DOWNLOAD_WAIT,
      );
      return;
    }

    if (ids.length === 0) {
      ToastService.info(
        [ToastService.MESSAGE.DLN_DOWNLOAD_NONE_SELECTED],
        ToastService.ID.DLN_DOWNLOAD_NONE_SELECTED,
      );
      return;
    }

    // Don't allow downloading more than 300 dlns because browser will run out of memory
    if (ids.length > 300) {
      ToastService.warning(
        [ToastService.MESSAGE.DLN_DOWNLOAD_TOO_MANY_SELECTED],
        ToastService.ID.DLN_DOWNLOAD_TOO_MANY_SELECTED,
      );
      log.productAnalyticsEvent(
        'Too many delivery notes selected',
        LOG.FEATURE.PDF_DOWNLOAD,
        LOG.TYPE.FAILED_VALIDATION,
      );
      return;
    }

    // single delivery note that does not need to be zipped
    if (ids.length === 1) {
      log.productAnalyticsEvent(
        'Download delivery note PDF',
        LOG.FEATURE.PDF_DOWNLOAD,
      );

      this.exportingDeliveryNotes = true;

      const deliveryNoteId = ids[0];

      const promise = this.exportSpecificDeliveryNoteAsPDF(
        deliveryNoteId,
        options,
      );

      ToastService.promise(
        promise,
        ['PDF-Lieferung wird geladen...'],
        ['PDF-Lieferung konnte geladen werden.'],
        [
          'PDF-Lieferung konnte nicht geladen werden.',
          ToastService.MESSAGE.CONTACT_SUPPORT,
        ],
      );

      const [file, error] = await handlePromise(promise);

      if (error) {
        log.error({
          message: `Failed to export PDF of delivery note. id: ${deliveryNoteId}`,
          error,
        });
        log.productAnalyticsEvent(
          'Failed to download delivery note PDF',
          LOG.FEATURE.PDF_DOWNLOAD,
          LOG.TYPE.ERROR,
        );
        this.exportingDeliveryNotes = false;
        throw error;
      }

      downloadFile(
        file,
        this.getDeliveryNoteFileName({
          deliveryNoteId,
          deliveryNoteNumber: deliveryNoteNumbers[deliveryNoteId],
        }),
      );
    }

    if (ids.length > 1) {
      if (downloadOption === this.DOWNLOAD_OPTION.MERGE) {
        log.productAnalyticsEvent(
          'Download multiple delivery note PDFs as one merged document',
          LOG.FEATURE.PDF_DOWNLOAD,
        );
      }

      this.exportingDeliveryNotes = true;

      let loadingMessage = 'PDF-Lieferungen werden geladen...';
      if (ids.length >= 10) {
        loadingMessage =
          'PDF-Lieferungen werden geladen. Dies kann einige Sekunden dauern...';
      }

      if (ids.length >= 30) {
        loadingMessage =
          'PDF-Lieferungen werden geladen. Dies kann einige Minuten dauern...';
      }

      const promise = this.exportMultipleDeliveryNotesAsPDF(ids, options);

      // It is necessary to store the toast information in a global variable
      // because the toast needs to accessed from inside the promise from which it is created.
      // The current solution with the global this.multiDeliveryNoteDownloadToast variable is not a clean solution.
      this.multiDeliveryNoteDownloadToast = {
        errorLines: [
          'PDF-Lieferungen konnten nicht geladen werden.',
          ToastService.MESSAGE.CONTACT_SUPPORT,
        ],
        id: 'multiDeliveryNoteDownloadToast-id',
        loadingLines: [loadingMessage],
        promise,
        successLines: ['PDF-Lieferungen konnten geladen werden.'],
      };

      ToastService.promise(
        this.multiDeliveryNoteDownloadToast.promise,
        [
          `${this.multiDeliveryNoteDownloadToast.loadingLines[0]} (0/${
            ids.length
          })`,
        ],
        this.multiDeliveryNoteDownloadToast.successLines,
        this.multiDeliveryNoteDownloadToast.errorLines,
        this.multiDeliveryNoteDownloadToast.id,
      );

      const [response, error] = await handlePromise(promise);

      if (error) {
        log.error({
          message: 'Failed to export PDF of delivery notes.',
          error,
        });
        log.productAnalyticsEvent(
          'Failed to download delivery note PDF',
          LOG.FEATURE.PDF_DOWNLOAD,
          LOG.TYPE.ERROR,
        );
        this.exportingDeliveryNotes = false;
        throw error;
      }

      const [blobArray, failedPdfs] = response;

      if (failedPdfs.length > 0) {
        const numbers = failedPdfs.map((dlnId) => dlnId);

        if (failedPdfs.length === 1) {
          ToastService.error([
            `Download fehlgeschlagen für die Lieferung ${numbers.join(', ')}.`,
            ToastService.MESSAGE.CONTACT_SUPPORT,
          ]);
          log.productAnalyticsEvent(
            'Failed to download delivery note PDF',
            LOG.FEATURE.PDF_DOWNLOAD,
            LOG.TYPE.ERROR,
          );
        } else {
          ToastService.error([
            `Download fehlgeschlagen für die Lieferungen ${numbers.join(
              ', ',
            )}.`,
            ToastService.MESSAGE.CONTACT_SUPPORT,
          ]);
          log.productAnalyticsEvent(
            'Failed to download multiple delivery note PDFs',
            LOG.FEATURE.PDF_DOWNLOAD,
            LOG.TYPE.ERROR,
          );
        }
      }

      if (
        blobArray.length > 0 &&
        downloadOption === this.DOWNLOAD_OPTION.MERGE
      ) {
        this.mergeFiles(
          blobArray,
          this.ENTITIES.DELIVERY_NOTES,
          this.FILE_FORMATS.PDF,
        );
      }
    }

    this.exportingDeliveryNotes = false;
  };

  // merge the pdfs into one file
  async mergeFiles(blobArray, entityType, format) {
    const merger = new PDFMerger();

    for (const element of blobArray) {
      await merger.add(element.file);
    }

    const mergedPdf = await merger.saveAsBlob();

    downloadFile(
      mergedPdf,
      this.generateFileName({
        entityType,
        format,
      }),
    );
  }

  /**
   * Export the data as an Excel file.
   *
   * @param {Array} data - The data to export.
   * @param {Object} options - The options for the exported file.
   * @param {string | undefined} options.predefinedRange - The predefined date range string to use for the exported file.
   * @param {Date[] | undefined} options.customRange - The custom date range to use for the exported file.
   * @param {string | undefined} options.entityType - The entity type to use for the exported file.
   */
  exportExcel = (data, options) => {
    // Don't log product analytics event here because it is already logged in the functions calling exportExcel
    // log.productAnalyticsEvent('Download Excel', LOG.FEATURE.EXCEL_DOWNLOAD);

    // Create a new Work Sheet using the data stored in an Array of Arrays.
    const workSheet = utilsXLSX.aoa_to_sheet(data);
    // Generate a Work Book containing the above sheet.
    const workBook = {
      SheetNames: ['data'],
      Sheets: { cols: [], data: workSheet },
    };

    // Exporting the file with the desired name and extension.
    const excelBuffer = writeXLSX(workBook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const fileData = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
    });

    const fileNamingOptions = {
      entityType: options?.entityType ?? this.ENTITIES.INVOICES,
      format: this.FILE_FORMATS.XLSX,
    };

    if (options?.predefinedRange && options?.predefinedRange !== 'individual') {
      fileNamingOptions.predefinedRange = options.predefinedRange;
    } else if (options?.customRange) {
      fileNamingOptions.date = this.generateDateStringFromDateRange(
        options.customRange,
      );
    }

    FileSaver.saveAs(fileData, this.generateFileName(fileNamingOptions));
  };

  /**
   * Export the data as an Excel file from a blob.
   *
   * @param {Blob} fileData - The blob to export.
   * @param {Object} options - The options for the export file.
   * @param {string} options.entityType - The entity type to use for the exported file.
   * @param {string} options.predefinedRange - The predefined date range string to use for the exported file.
   * @param {Date[]} options.customRange - The custom date range to use for the exported file.
   */
  exportExcelFromBlob = (fileData, options) => {
    const fileNamingOptions = {
      entityType: options?.entityType ?? this.ENTITIES.DELIVERIES,
      format: this.FILE_FORMATS.XLSX,
    };

    if (options?.predefinedRange && options?.predefinedRange !== 'individual') {
      fileNamingOptions.predefinedRange = options.predefinedRange;
    } else if (options?.customRange) {
      fileNamingOptions.date = this.generateDateStringFromDateRange(
        options.customRange,
      );
    }

    FileSaver.saveAs(fileData, this.generateFileName(fileNamingOptions));
  };

  /**
   * Export the data as a CSV file from a blob.
   *
   * @param {Blob} fileData - The blob to export.
   * @param {Object} options - The options for the export file.
   * @param {string} options.entityType - The entity type to use for the exported file.
   * @param {string} options.predefinedRange - The predefined date range string to use for the exported file.
   * @param {Date[]} options.customRange - The custom date range to use for the exported file.
   */
  exportCSVFromBlob = (fileData, options) => {
    const fileNamingOptions = {
      entityType: options.entityType ?? this.ENTITIES.DELIVERIES,
      format: this.FILE_FORMATS.CSV,
    };

    if (options?.predefinedRange && options?.predefinedRange !== 'individual') {
      fileNamingOptions.predefinedRange = options.predefinedRange;
    } else if (options?.customRange) {
      fileNamingOptions.date = this.generateDateStringFromDateRange(
        options.customRange,
      );
    }

    FileSaver.saveAs(fileData, this.generateFileName(fileNamingOptions));
  };

  getDeliveryNoteFileName({
    deliveryNoteDate,
    deliveryNoteId,
    deliveryNoteNumber,
    fileFormat = 'pdf',
    index,
  }) {
    const number = deliveryNoteNumber ?? index ?? '';
    const date = dateUtils.getFormattedDate(
      deliveryNoteDate,
      dateUtils.DATE_FORMAT.YYYYMMDD,
    );

    return this.generateFileName({
      date,
      entityType: this.ENTITIES.DELIVERY,
      filterGroupName: number,
      format: fileFormat,
    });
  }

  getInvoiceFileName(invoice, index, fileFormat, extraNameParts = []) {
    if (invoice?.originalFilename) {
      return invoice.originalFilename;
    }

    const number = invoice?.number ?? index ?? '';
    const date =
      invoice?.date &&
      dateUtils.getFormattedDate(invoice?.date, dateUtils.DATE_FORMAT.YYYYMMDD);

    return this.generateFileName({
      date,
      entityType: this.ENTITIES.INVOICE,
      extraNameParts,
      filterGroupName: number,
      format: fileFormat,
    });
  }

  ENTITIES = {
    CONTACT_POINTS: i18n.t('export.entity.contactPoints'),
    DATA_EXCHANGE: i18n.t('export.entity.dataExchange'),
    DATA_SUBSCRIPTIONS: i18n.t('export.entity.dataSubscriptions'),
    DELIVERIES: i18n.t('export.entity.deliveries'),
    DELIVERY_NOTE: i18n.t('export.entity.deliveryNote'),
    DELIVERY_NOTES: i18n.t('export.entity.deliveryNotes'),
    DELIVERY: i18n.t('export.entity.delivery'),
    EMAIL: i18n.t('export.entity.email'),
    INCOMING_INVOICES: i18n.t('export.entity.incomingInvoices'),
    INVOICE: i18n.t('export.entity.invoice'),
    INVOICE_EMAIL: i18n.t('export.entity.invoiceEmail'),
    INVOICES: i18n.t('export.entity.invoices'),
    NOTIFICATION_POLICIES: i18n.t('export.entity.notificationPolicies'),
    OUTGOING_INVOICES: i18n.t('export.entity.outgoingInvoices'),
    PRODUCT_ANALYTICS: i18n.t('export.entity.productAnalytics'),
    REPORT: i18n.t('export.entity.report'),

    // Settings entity translations
    article: i18n.t('export.entity.article'),
    category: i18n.t('export.entity.category'),
    company: i18n.t('export.entity.company'),
    costCenter: i18n.t('export.entity.costCenter'),
    customField: i18n.t('export.entity.customField'),
    invoiceCheckIgnoredArticle: i18n.t(
      'export.entity.invoiceCheckIgnoredArticle',
    ),
    organisationalGroup: i18n.t('export.entity.organisationalGroup'),
    signatureField: i18n.t('export.entity.signatureField'),
    site: i18n.t('export.entity.site'),
    user: i18n.t('export.entity.user'),
    userGroup: i18n.t('export.entity.userGroup'),
    vehicle: i18n.t('export.entity.vehicle'),
  };

  static PREFIX = 'Vestigas';
  FILE_FORMATS = {
    CSV: 'csv',
    PDF: 'pdf',
    XLSX: 'xlsx',
  };

  /**
   * Generates a file name based on the entity type, date, predefined range, filter group name, format and if it is an archive
   *
   * @param {Object} options
   * @param {string} options.entityType - The entity type
   * @param {string | null} [options.date] - The date - If null is passed then date is omitted
   * @param {string} [options.predefinedRange] - The predefined range
   * @param {string} [options.filterGroupName] - The filter group name
   * @param {string} options.format - The format
   * @param {boolean} [options.isArchive] - If it is an archive
   * @param {string[]} [options.extraNameParts] - Extra name parts to add to the file name
   * @returns {string}
   */
  generateFileName({
    entityType,
    date,
    predefinedRange,
    filterGroupName,
    format,
    isArchive = false,
    extraNameParts = [],
  }) {
    const parts = [ExportService.PREFIX];

    parts.push(entityType);

    if (date) {
      parts.push(date);
    } else if (predefinedRange) {
      parts.push(dateRangeOptions[predefinedRange]);
    }

    if (filterGroupName) {
      parts.push(filterGroupName);
    }

    if (date !== null && !date) {
      parts.push(
        `${dateUtils.getFormattedDate(new Date(), dateUtils.DATE_FORMAT.YYYY_MM_DD)}`,
      );
    }

    let fileName = parts.join('_');

    for (const data of extraNameParts.filter((data) => data)) {
      fileName += `_${data}`;
    }

    if (format) {
      fileName += '.';
      fileName += format;
    }

    if (isArchive) {
      fileName += '.zip';
    }

    return fileName;
  }

  /**
   * Generates a string from a date range array
   *
   * @param {Date[]} dateRangeArray - Should include 2 dates, from and to in that order
   * @returns {string}
   */
  generateDateStringFromDateRange(dateRangeArray) {
    const fromDate = dateUtils.getFormattedDate(
      dateRangeArray[0],
      dateUtils.DATE_FORMAT.YYYY_MM_DD,
    );
    const toDate = dateUtils.getFormattedDate(
      dateRangeArray[1],
      dateUtils.DATE_FORMAT.YYYY_MM_DD,
    );

    return `Von-${fromDate}-bis-${toDate}`;
  }
}

const exportServiceInstance = new ExportService();

export default exportServiceInstance;
